import { GuestDataStore } from '@/features/shared/constants/global'
import { removeCookie, setCookie } from '@/features/shared/utils/cookies'
import { EMPTY_GUEST_PARAMS } from '@/features/account/services/GuestUser/constants'
import { guestAddressCookie, guestStoreCookie } from '@/features/account/services/GuestUser/cookies'
import { guestParamsQueryOptions } from '@/features/account/services/GuestUser/queryOptions'
import {
  deleteGuestCookies,
  postGuestCookies,
} from '@/features/account/services/GuestUser/fetchers'
import { guestClientCookieAttributes } from '@shared/constants/cookies'
import { logWarn } from '@/features/shared/utils/logger'
import { type GuestUserParams } from '@/features/account/services/GuestUser/types'
import { type GuestStore } from '@/features/shop/services/ShoppingStore/types'
import { type ValidAddress } from '@/features/account/services/Addresses/types'
import { type QueryClient } from '@tanstack/react-query'
import { type RequestCookies } from '@shared/request_type'

export const parseGuestCookies = (cookies: RequestCookies): GuestUserParams => ({
  guestStore: guestStoreCookie.from(cookies),
  guestAddress: guestAddressCookie.from(cookies),
})

// keep the guest store cookie small
export const slimGuestStore = (store: GuestStore): GuestStore => ({
  id: store.id,
  name: store.name,
  metro_id: store.metro_id,
  is_pickup: store.is_pickup,
  store_location_id: store.store_location_id,
})

const setGuestParamsQueryData = (
  nextParams: Partial<GuestUserParams>,
  queryClient: QueryClient,
) => {
  queryClient.setQueryData(guestParamsQueryOptions.queryKey, (prev) => ({
    ...(prev ?? EMPTY_GUEST_PARAMS),
    ...nextParams,
  }))
}

export const setGuestStoreCookie = (store: GuestStore, queryClient: QueryClient) => {
  const guestStore = slimGuestStore(store)
  setCookie(GuestDataStore.STORE, guestStore, guestClientCookieAttributes)
  setGuestParamsQueryData({ guestStore }, queryClient)
}

// Write the httpOnly cookie first, then update the cache. Not optimistic on purpose:
// the cache drives isGuestUser, which can trigger navigation (e.g. the ungated homepage
// redirect to GHP). If the cache flipped before the cookie existed, that navigation's SSR
// would see no address and overwrite it with the inferred zip.
export const persistGuestAddress = async (guestAddress: ValidAddress, queryClient: QueryClient) => {
  const params = await postGuestCookies({ address: guestAddress })
  // cancel in-flight fetches so a late response can't clobber our write
  await queryClient.cancelQueries({ queryKey: guestParamsQueryOptions.queryKey })
  queryClient.setQueryData(guestParamsQueryOptions.queryKey, params)
}

export const clearGuestCookies = async (queryClient: QueryClient): Promise<void> => {
  // cancel in-flight fetches so a late response can't re-add the address
  await queryClient.cancelQueries({ queryKey: guestParamsQueryOptions.queryKey })
  // Store cookie is client-owned; the httpOnly address cookie is cleared by the server
  // (this DELETE, and the proxy on logout), so callers don't need to await this.
  removeCookie(GuestDataStore.STORE)
  queryClient.setQueryData(guestParamsQueryOptions.queryKey, EMPTY_GUEST_PARAMS)
  return deleteGuestCookies()
    .then(() => undefined)
    .catch(() => {
      logWarn('clearGuestCookies: failed to clear the httpOnly guest address cookie')
    })
}
